// dependencies
import * as Sentry from "@sentry/react";
import { List, Map, fromJS } from "immutable";
import moment from "moment";
import { GenerateProcess } from "redux-process";

import Config from "./Config";

// api
import { CycleGet, EditReviewCycle } from "../../../api/reviews";

import { DisplayConfirmationModal } from "../../../actions/modals.actions";
import { DisplayError, DisplaySuccess } from "../../../actions/notifications.actions";
// actions
import { StateChange } from "../../../actions/page.actions";

import { getReviewCycleFormByName } from "../../../selectors/reviewCycles";
// selectors
import { getCompanyLimit, getCompanySettingsContext } from "../../../selectors/settings";
import {
	getBulkEditGroupInputNames,
	getBulkEditGroupState,
	getBulkEditingForm,
	getBulkUpdatesRequestBody,
	getCyclePayload,
	getEditingForms,
	getLocalState,
	getReviewTemplateForms as getLocalTemplateForms,
	getReviewCycleID,
	getStartDateTime,
} from "./selectors";

// utils
import uuid from "../../../utils/GenerateUUID";
import momentize from "../../../utils/Momentize";
import Socket from "../../../utils/Socket";

// config
import ui from "./ui";

/**
 * Initialize the page
 */
export const init = () => async (dispatch, getState) => {
	// init the ui
	dispatch(StateChange(Config.pageName, [], ui));

	// get review cycle forms
	const state = getState();
	const reviewCycleID = getReviewCycleID(state);
	const request = await dispatch(GenerateProcess("getReviewCycleForms", { reviewCycleID }));
	dispatch(
		GenerateProcess("GetObjectPermissions", {
			domain: "review-cycles",
			objectName: "reviewCycleID",
			objectValue: reviewCycleID,
		})
	);

	const augmentedEasyAddForms = request.response.get("easyAddForms", List()).withMutations((mutableForms) => {
		mutableForms.forEach((form, ii) => {
			mutableForms.setIn([ii, "id"], form.get("_id"));
			mutableForms.setIn([ii, "type"], "easyAdd");
		});
	});

	const mutatedUI = ui.withMutations((mutable) => {
		mutable.set("templateForms", fromJS(augmentedEasyAddForms));
	});

	dispatch(StateChange(Config.pageName, [], mutatedUI));
};

/**
 * Choose a form to bulk edit fields
 */
export const selectFormForBulkEditing = (formIdentifier) => (dispatch, getState) => {
	const state = getState();
	const reviewCycleID = getReviewCycleID(state);
	const formProps = getReviewCycleFormByName(state, reviewCycleID, formIdentifier, null);

	const nextState = getLocalState(state).withMutations((mutable) => {
		mutable.setIn(["bulkEditForm"], formIdentifier);
		mutable.setIn(["bulkEditFormProps"], formProps);
	});

	dispatch(StateChange(Config.pageName, [], nextState));
};

/**
 * Enable/Disable bulk edit group. If disabling, reset the values of the inputs
 * related to that group.
 */
export const toggleBulkEditGroup = (groupName) => (dispatch, getState) => {
	const state = getState();
	const nextGroupState = !getBulkEditGroupState(state, groupName);

	const nextLocalState = getLocalState(state).withMutations((mutable) => {
		if (nextGroupState === false) {
			const inputNames = getBulkEditGroupInputNames(state, groupName);

			// reset input values in group
			inputNames.forEach((inputName) => {
				const defaultValue = ui.getIn(["bulkEditInputs", inputName]);
				mutable.setIn(["bulkEditInputs", inputName], defaultValue);
			});
		}

		// update the group editing state
		mutable.setIn(["bulkEditGroups", groupName, "isEditing"], nextGroupState);
	});

	dispatch(StateChange(Config.pageName, [], nextLocalState));
};

/**
 * Resets the bulk update form.
 */
export const resetBulkUpdateForm = () => (dispatch, getState) => {
	const state = getState();
	const nextState = getLocalState(state).withMutations((mutable) => {
		mutable.set("bulkEditForm", null);
		mutable.set("bulkEditFormProps", null);
		mutable.set("bulkEditGroups", ui.get("bulkEditGroups"));
		mutable.set("bulkEditInputs", ui.get("bulkEditInputs"));
	});

	dispatch(StateChange(Config.pageName, [], nextState));
};

/**
 * Send updates to server for bulk updating.
 */
export const sendBulkUpdatesToServer = () => async (dispatch, getState) => {
	const state = getState();
	const formName = getBulkEditingForm(state);
	const reviewCycleID = getReviewCycleID(state);
	const formUpdates = getBulkUpdatesRequestBody(state);

	const payload = {
		formName,
		updates: formUpdates,
	};

	const confirmationMessage = formUpdates.map((update) => {
		let { value } = update;
		const { type } = Config.bulkUpdateInputAttributes[update.field];

		if (type === "date") {
			const momentValue = moment(value);
			value = moment.isMoment(momentValue) ? moment(value).format(Config.confirmationDateFormat) : null;
		} else if (update.field === "visible") {
			return value ? "forms will be visible to subject" : "forms will not be visible to subject";
		} else if (update.field === "redacted") {
			return value ? "authors will be redacted" : "authors will not be redacted";
		}

		return `${Config.bulkUpdateInputAttributes[update.field].label} is ${value}`;
	});

	dispatch(
		DisplayConfirmationModal(
			`You will be updating these attributes for ${formName}: ${confirmationMessage.join(", ")}. Are you sure?`,
			async () => {
				const request = await dispatch(
					GenerateProcess("bulkUpdateReviewCycleForms", {
						reviewCycleID,
						payload,
					})
				);

				if (request.succeeded) {
					dispatch(DisplaySuccess("Finished updating."));
					dispatch(resetBulkUpdateForm());
				}
			}
		)
	);
};

/**
 * Updates the local state with the value for the input.
 *
 * @param {string} inputName - name of input
 * @param {any} value - next value of the input
 */
export const changeBulkEditInput = (inputName, value) => (dispatch) => {
	dispatch(StateChange(Config.pageName, ["bulkEditInputs", inputName], value));
};

export function GetCycle(cycleId) {
	return (dispatch) => {
		/*
		 */

		const callback = (response) => {
			if (response.status === 2) {
				dispatch(
					StateChange(
						Config.pageName,
						["inputs"],
						Map({
							cyclename: response.data.name,
							startdate: momentize(response.data.start_date),
						})
					)
				);
			}
		};

		dispatch(CycleGet(cycleId, callback));
	};
}

export function EditCycle(cycleId, payload) {
	return (dispatch, getState) => {
		const callback = (response) => {
			if (response.status === 2) {
				dispatch(DisplaySuccess("Updated."));

				const state = getState();
				const reviewCycleID = getReviewCycleID(state);
				dispatch(CycleGet(reviewCycleID));
			}
		};

		dispatch(EditReviewCycle(cycleId, payload, callback));
	};
}

/**
 * Remove a form.
 * @param {string} formID - id of the form to be removed
 */
export const removeForm = (formID) => (dispatch, getState) => {
	const state = getState();
	const forms = getLocalTemplateForms(state);

	dispatch(
		StateChange(
			Config.pageName,
			["templateForms"],
			forms.filter((form) => form.get("id") !== formID)
		)
	);
};

/**
 * This will append a new form to the forms list. It will also mark that form
 * as being edited.
 *
 * @param {Map} form - selected form from the form picker {id, name}
 */
export const appendNewForm = (form, formType) => (dispatch, getState) => {
	const state = getState();
	let startDateTime = getStartDateTime(state);
	const defaultDueTime = getCompanySettingsContext(state).getIn(["reviews", "default_review_cycle_due_time"], 17);

	if (!startDateTime) {
		startDateTime = moment().hour(17).minute(0).second(0).millisecond(0);
	}

	const formID = form.get("id");
	const formName = form.get("name");
	const uniqueFormID = uuid();
	let newForm = {};

	if (formType === "basic") {
		newForm = fromJS({
			id: uniqueFormID,
			type: formType,
			appendToTemplate: true, // form was asdded outside of template
			form: { id: formID, name: formName },
			assignment: {
				authors: [],
				signers: [],
				visibleToSubject: false,
				redacted: false,
				authorsDueDate: startDateTime.clone().hour(defaultDueTime),
				signersDueDate: startDateTime.clone().add(1, "days").hour(defaultDueTime),
			},
			unsaved: true,
			isNewForm: true,
		});
	}

	if (formType === "external") {
		newForm = fromJS({
			id: uniqueFormID,
			type: "external",
			appendedToTemplate: true,
			form: { id: formID, name: formName },
			assignment: {
				authorName: "",
				authorEmail: "",
				emailMessage: "",
				visibleToSubject: false,
				redacted: false,
			},
			unsaved: true,
			isNewForm: true,
		});
	}

	if (formType === "easyAdd") {
		newForm = fromJS({
			id: uniqueFormID,
			type: "easyAdd",
			appendedToTemplate: true,
			formObject: {
				_id: formID,
				name: formName,
			},
			who_can_add_form: {
				any_employee: true,
				subject: false,
				manager_or_chain_of_command: false,
				admin_or_group_admin: false,
			},
			subject_employee_choose_author: "",
			who_signs_off: [],
			author_due_date: startDateTime.clone().hour(defaultDueTime),
			signer_due_date: startDateTime.clone().add(1, "days").hour(defaultDueTime),
			due_date_types: "static",
			redact: false,
			visibility: false,
			allow_comments: false,
			is_active: true,
			requiresApproval: null,
			unsaved: true,
			isNewForm: true,
		});
	}

	const localState = getLocalState(state);
	const templateForms = getLocalTemplateForms(state);
	const editingFormsList = getEditingForms(state);
	const maxFormsCount = getCompanyLimit(state, "MAXFORMSPERREVIEWTEMPLATE", 100);

	// add limit
	if (templateForms.size >= maxFormsCount) {
		return;
	}

	const nextState = localState.withMutations((mutable) => {
		mutable.set("templateForms", templateForms.push(newForm));
		mutable.set("editingForms", editingFormsList.push(uniqueFormID));
	});

	// update store
	dispatch(StateChange(Config.pageName, [], nextState));
};

/**
 * Updates the list of forms the user is editing.
 */
export const updateEditingForms = (formsList) => (dispatch) => {
	dispatch(StateChange(Config.pageName, ["editingForms"], formsList));
};

/**
 * Updates a form assignment with the updated state.
 */
export const updateTemplateAssignment = (formID, updatedAssignment) => (dispatch, getState) => {
	const state = getState();

	const allForms = getLocalTemplateForms(state);

	const forms = allForms.update(
		getLocalTemplateForms(state).findIndex((form) => form.get("id") === formID),
		(form) => {
			const formType = form.get("type");

			if (formType === "basic" || formType === "external") {
				// get the current assignment object
				const currentAssignment = form.get("assignment", Map());
				// merge the assignments
				const updatedForm = currentAssignment.merge(fromJS(updatedAssignment));
				return form.withMutations((mutable) => {
					mutable.set("assignment", updatedForm);
					mutable.set("unsaved", false);
					mutable.set("isNewForm", false);
				});
			}

			if (formType === "easyAdd") {
				const updatedForm = form.merge(fromJS(updatedAssignment));
				return updatedForm.withMutations((mutable) => {
					mutable.set("unsaved", false);
					mutable.set("isNewForm", false);
				});
			}
			return null;
		}
	);

	// if selected form and who can add already exists,
	// display error popup
	dispatch(StateChange(Config.pageName, ["templateForms"], forms));

	// remove the form from the list of editing forms
	const editingForms = getEditingForms(state);

	dispatch(updateEditingForms(editingForms.filter((id) => id !== formID)));
};

export function OpenSocket(reviewCycleID) {
	return (dispatch, getState) => {
		const socket = Socket(`/api/review_cycle/${reviewCycleID}/status/`);

		socket.on("message", (message) => {
			const percentDone = (message.data.progress / message.data.total) * 100;

			dispatch(StateChange(Config.pageName, ["isBuilding"], !message.data.finished));

			dispatch(StateChange(Config.pageName, ["buildProgress"], percentDone));

			if (message.data.finished === true) {
				dispatch(StateChange(Config.pageName, ["isBuilding"], false));

				dispatch(GenerateProcess("getReviewCycleForms", { reviewCycleID }));

				const currentState = getState();

				const localState = getLocalState(currentState);

				dispatch(StateChange(Config.pageName, [], localState));

				socket.close();
			}
		});
	};
}

export const addFormToCycle = (formID, cycleID, formType) => async (dispatch, getState) => {
	const currentState = getState();
	const payload = getCyclePayload(currentState, formType);
	const reviewCycleID = getReviewCycleID(currentState);
	payload.cycleID = cycleID;

	dispatch(
		DisplayConfirmationModal(
			"Are you sure you want to add this form? This will update every review in the cycle to include this form. Any previously completed or closed reviews will be re-opened.",
			async () => {
				const request = await dispatch(GenerateProcess("addFormToCycle", { payload }));

				if (request.succeeded) {
					// set isBuiling to true, so that ProgressBar appears.
					// But first reset buildProgress to 0
					dispatch(StateChange(Config.pageName, ["buildProgress"], 0));
					dispatch(StateChange(Config.pageName, "isBuilding", true));
					dispatch(StateChange("ReviewCycleView", "isBuilding", true));

					// Uses the same handler as view cycle to check progress
					const socket = Socket(`/api/review_cycle/${reviewCycleID}/status/`);

					socket.on("message", (message) => {
						// calc the progress.
						const percentDone = (message.data.progress / message.data.total) * 100;
						// update
						dispatch(StateChange(Config.pageName, ["buildProgress"], percentDone));
						dispatch(StateChange("ReviewCycleView", ["buildProgress"], percentDone));

						if (message.data.finished === true) {
							// If async_job is completed, get forms
							// so that user sees the new form without reloading page
							dispatch(
								GenerateProcess("getReviewCycleForms", {
									reviewCycleID,
								})
							);

							// Set isBuilding to False so that Progress Bar
							// disappears and mainBulkEditFormComponent is rendered.
							dispatch(StateChange(Config.pageName, ["isBuilding"], false));

							// Set state to include new form
							// and close add form options
							const newState = getState();

							const newLocalState = getLocalState(newState);
							const newTemplateForms = getLocalTemplateForms(newState).filter(
								(form) => form.get("id") !== formID
							);

							const finalState = newLocalState.withMutations((mutable) => {
								mutable.set("templateForms", newTemplateForms);
								mutable.set("editingForms", List());
							});
							dispatch(StateChange(Config.pageName, [], finalState));
							socket.close();

							// display success message
							dispatch(DisplaySuccess("Form added to review cycle."));
						}
					});
				}
			}
		)
	);
};

export const deleteFormsFromCycle = (baseFormID, formName) => async (dispatch, getState) => {
	const currentState = getState();
	const reviewCycleID = getReviewCycleID(currentState);

	// use baseformID since formIds may change
	const payload = {
		baseFormID,
		formName,
		cycleID: reviewCycleID,
	};

	dispatch(
		DisplayConfirmationModal(
			"Are you sure you want to remove this review form? This will permanently remove the form from all reviews in this cycle regardless of prior assignment and completion. This action is permanent and cannot be undone.",
			async () => {
				const request = await dispatch(GenerateProcess("deleteFormsFromCycle", { payload }));

				if (request.succeeded) {
					// set isBuiling to true, so that ProgressBar appears.
					// But first reset buildProgress to 0
					dispatch(StateChange(Config.pageName, "buildProgress", 0));
					dispatch(StateChange(Config.pageName, "isBuilding", true));
					dispatch(StateChange("ReviewCycleView", "isBuilding", true));

					const socket = Socket(`/api/review_cycle/${reviewCycleID}/status/`);

					socket.on("message", (message) => {
						// calc the progress
						const percentDone = (message.data.progress / message.data.total) * 100;

						dispatch(StateChange(Config.pageName, ["buildProgress"], percentDone));
						dispatch(StateChange("ReviewCycleView", ["buildProgress"], percentDone));

						if (message.data.finished === true) {
							dispatch(
								GenerateProcess("getReviewCycleForms", {
									reviewCycleID,
								})
							);

							dispatch(StateChange(Config.pageName, ["isBuilding"], false));

							const newState = getState();

							const newLocalState = getLocalState(newState);

							dispatch(StateChange(Config.pageName, [], newLocalState));
							socket.close();
							dispatch(DisplaySuccess("Form removed from review cycle."));
						}
					});
				}
			}
		)
	);
};

export const replaceFormInCycle = (replace, replacement) => async (dispatch, getState) => {
	const currentState = getState();
	const reviewCycleID = getReviewCycleID(currentState);

	// uses baseformID for replace but not replacement since formIds may
	// change for an existing form, but replacent is most recent formid
	const payload = {
		replace,
		replacement,
		cycleID: reviewCycleID,
	};

	dispatch(
		DisplayConfirmationModal(
			"Are you sure you want to replace this review form? This will permanently change the form that will be filled out when the review starts.",
			async () => {
				const request = await dispatch(GenerateProcess("replaceFormsInCycle", { payload }));

				if (request.succeeded) {
					// set isBuiling to true, so that ProgressBar appears.
					// But first reset buildProgress to 0
					dispatch(StateChange(Config.pageName, "buildProgress", 0));
					dispatch(StateChange(Config.pageName, "isBuilding", true));
					dispatch(StateChange("ReviewCycleView", "isBuilding", true));

					const socket = Socket(`/api/review_cycle/${reviewCycleID}/status/`);

					socket.on("message", (message) => {
						// calc the progress
						const percentDone = (message.data.progress / message.data.total) * 100;

						dispatch(StateChange(Config.pageName, ["buildProgress"], percentDone));
						dispatch(StateChange("ReviewCycleView", ["buildProgress"], percentDone));

						if (message.data.finished === true) {
							dispatch(
								GenerateProcess("getReviewCycleForms", {
									reviewCycleID,
								})
							);

							dispatch(StateChange(Config.pageName, ["isBuilding"], false));

							const newState = getState();

							const newLocalState = getLocalState(newState);

							dispatch(StateChange(Config.pageName, [], newLocalState));
							socket.close();
							dispatch(DisplaySuccess("Form replaced successfully."));
						}
					});
				}
			}
		)
	);
};

// eslint-disable-next-line consistent-return
export const addEasyAdd = (formID, cycleID, payload) => async (dispatch) => {
	try {
		const request = await dispatch(GenerateProcess("addEasyAdd", { payload, cycleID }));

		if (request.status === 200) {
			dispatch(updateTemplateAssignment(formID, payload));
			dispatch(DisplaySuccess("Form added to review cycle."));
			dispatch(init());
		} else {
			dispatch(DisplayError("Form could not be added."));
		}
	} catch (ermahgerd) {
		dispatch(DisplayError("Form could not be added."));
		Sentry.captureException(ermahgerd);
	}
};

export const editEasyAdd = (cycleID, easyAddID, payload) => async (dispatch) => {
	try {
		const request = await dispatch(GenerateProcess("editEasyAdd", { payload, cycleID, easyAddID }));
		if (request.status === 200) {
			dispatch(updateTemplateAssignment(easyAddID, payload));
			dispatch(DisplaySuccess("Form updated successfully."));
		} else {
			dispatch(DisplayError("Form could not be added."));
		}
	} catch (ermahgerd) {
		dispatch(DisplayError("Form could not be added."));
		Sentry.captureException(ermahgerd);
	}
};

export const deleteEasyAdd = (cycleID, easyAddID) => async (dispatch) => {
	try {
		const request = await dispatch(GenerateProcess("deleteEasyAdd", { cycleID, easyAddID }));
		if (request.status === 200) {
			dispatch(DisplaySuccess("Form deleted successfully."));
			dispatch(init());
		} else {
			dispatch(DisplayError("Form could not be deleted."));
		}
	} catch (ermahgerd) {
		dispatch(DisplayError("Form could not be deleted."));
		Sentry.captureException(ermahgerd);
	}
};
